'use es6';

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'data-fetching-client';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import { useReportAccessSettings } from '../../../data/hooks/useReportAccessSettings';
import { REPORT_ACCESS_SETTINGS_FIELD_NAME, UPDATE_REPORT_ACCESS_SETTINGS } from '../../../data/requests/report';
import { getId, getName } from '../../../data/schemas/reportSchema';
import { useHideDialog } from '../../../dialog/ducks';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { useUserRoleForReport } from '../../../utils/trackingUtils';
import { getAccessSettingUsageProps } from '../../../permissions/permissionTrackingUtils';
import { PermissionSettingsProvider } from '../../shared/permissionSettings/context/PermissionSettingsContext';
import { ACTION_STATUSES } from '../../shared/actionStatuses';
import { ASSET_TYPES, PERMISSION_SETTINGS_USES } from '../../shared/permissionSettings/constants';
import { getDefaultSavedReportActionEventProperties } from '../../report-action-wrapper';
import ManageAccessDialog from './ManageAccessDialog';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
const ManageReportAccessDialogContainer = ({
  appUsageTracker,
  filteredReportCount,
  filters,
  onManageReportAccessSuccess = noop,
  reports
}) => {
  const hideDialog = useHideDialog();
  const usageTracker = useTracker(appUsageTracker);
  const firstReport = !reports.isEmpty() && reports.first();
  const isSelectAll = reports.isEmpty() && filteredReportCount;
  const count = isSelectAll ? filteredReportCount : reports.size;
  const reportName = firstReport ? getName(firstReport) : null;
  const {
    data: reportAccessSettings,
    loading: reportAccessSettingsLoading
  } = useReportAccessSettings(count === 1 && firstReport ? getId(firstReport) : undefined);
  const [actionStatus, setActionStatus] = useState(ACTION_STATUSES.NOT_STARTED);
  const userRole = useUserRoleForReport(firstReport);
  const countDependentTrackingProps = count === 1 ? Object.assign({
    role: userRole
  }, getDefaultSavedReportActionEventProperties(firstReport)) : {};
  const [updateAccessSettings] = useMutation(UPDATE_REPORT_ACCESS_SETTINGS, {
    onCompleted: ({
      [REPORT_ACCESS_SETTINGS_FIELD_NAME]: accessConfig
    }) => {
      setActionStatus(ACTION_STATUSES.COMPLETE);
      if (usageTracker) {
        usageTracker.track(EVENTS.completeEditAssetAccess, Object.assign({
          actionType: 'edit',
          action: 'edit-access',
          assetType: 'report',
          step: 'complete',
          count
        }, countDependentTrackingProps, getAccessSettingUsageProps(accessConfig)));
      }
      onManageReportAccessSuccess();
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "reporting-action-components.actions.manageReportAccess.alert.success",
          options: {
            reportName,
            count
          }
        }),
        type: 'success',
        'data-test-id': 'report-save-success-alert'
      });
      hideDialog();
    },
    onError: error => {
      console.error(error);
      setActionStatus(ACTION_STATUSES.FAILED);
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.manageReportAccess.alert.failure",
          options: {
            count
          }
        }),
        type: 'danger'
      });
    }
  });
  if (reportAccessSettingsLoading) {
    return null;
  }
  const {
    assignmentMode,
    accessLevel,
    selectedUserSet,
    selectedTeamSet
  } = reportAccessSettings;
  const handleAccessSettingsUpdate = accessConfig => {
    updateAccessSettings({
      variables: {
        reportIds: reports.map(getId),
        accessConfig,
        filters
      }
    });
    setActionStatus(ACTION_STATUSES.PENDING);
  };
  return /*#__PURE__*/_jsx(PermissionSettingsProvider, {
    use: PERMISSION_SETTINGS_USES.EDIT,
    initialUserSet: selectedUserSet,
    initialTeamSet: selectedTeamSet,
    initialAccessLevel: accessLevel,
    initialAssignmentMode: assignmentMode,
    children: /*#__PURE__*/_jsx(ManageAccessDialog, {
      actionStatus: actionStatus,
      appUsageTracker: appUsageTracker,
      assetName: reportName,
      assetSpecificTrackingProps: countDependentTrackingProps,
      assetType: ASSET_TYPES.REPORT,
      assetCount: count,
      onConfirm: handleAccessSettingsUpdate
    })
  });
};
ManageReportAccessDialogContainer.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  filteredReportCount: PropTypes.number,
  filters: PropTypes.object,
  onManageReportAccessSuccess: PropTypes.func,
  reports: ImmutablePropTypes.set.isRequired
};
export default ManageReportAccessDialogContainer;